import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { cloneDeep } from 'lodash';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { App, Project, Site, User } from '../models';
import { SNACKBAR_CONSTANTS } from '../shared/constants';
import { ProjectsDataService } from './data-services';
import { LogicService, SmartsFormBuilderLogicService } from './logic-services';
import { BreadcrumbLogicService } from './logic-services/breadcrumb.service';
import { FormElement, ProjectsServiceModel, SmartsForm, UserProjectLists } from './logic-services/models';
import { SchemaBuilderUtility, UISchemaBuilderUtility } from './utility-services';

@Injectable({
    providedIn: 'root',
})
export class ProjectsService implements LogicService {

    private _projectChanged = new Subject<boolean>();
    private _dragAndDropUpdate = new Subject<boolean>();
    private _selectedProjectChanged = new Subject<boolean>();
    private _projectsReady: Subject<boolean> = new Subject<boolean>();
    private _projectOrdersUpdating = new BehaviorSubject<boolean>(false);

    projectChanged$ = this._projectChanged.asObservable();
    dragAndDropUpdate$ = this._dragAndDropUpdate.asObservable();
    projectsReady$ = this._projectsReady.asObservable();
    selectedProjectChanged$ = this._selectedProjectChanged.asObservable();

    model: ProjectsServiceModel;
    project: BehaviorSubject<Project> = new BehaviorSubject<Project>(null);
    projectLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    projects: BehaviorSubject<Project[]> = new BehaviorSubject<Project[]>(null);
    projectsLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    projectOrdersUpdating$ = this._projectOrdersUpdating.asObservable();

    get isProjectOrdersUpdating(): boolean {
        return this._projectOrdersUpdating.value;
    }

    get selectableProjects(): Project[] {
        return (this.validateAdmin() ? this.model?.adminProjects : this.model?.projects) ?? [];
    }

    constructor(
        private _projectDataService: ProjectsDataService,
        private _breadcrumbService: BreadcrumbLogicService,
        private _snackBar: MatSnackBar,
        private _uiSchemaBuilderUtility: UISchemaBuilderUtility,
        private _schemaBuilderUtility: SchemaBuilderUtility,
        private _smartsFormBuilderLogicService: SmartsFormBuilderLogicService,
        private _router: Router,
    ) { }

    initModel(): void {
        if (this.model === undefined) {
            this.model = new ProjectsServiceModel();
            this.model.exportingLayout = false;
            this.model.lastRegularProjectId = null;
            this.model.projects = new Array<Project>();
            this.model.adminProjects = new Array<Project>();
            this.model.project = new Project();
            this.model.projectPreview = new Project();
            this.model.projectAdmins = new Array<User>();
            this.model.savingOrPublishing = false;
            this.model.uploadingLayout = false;
            this.model.projectLoading = false;
            this.model.projectsLoading = false;
            this.model.virtualizationSettings = { itemHeight: 60, pageSize: 50 };
        }
    }

    getProjectNew(): Project {
        if (this.model.currentProject === undefined) {
            this.model.currentProject = new Project();
        }

        if (this.model.currentProject.JSONForms === undefined) {
            this.model.currentProject.JSONForms = new SmartsForm();
        }

        if (this.model.currentProject.layoutDraft === undefined) {
            this.model.currentProject.layoutDraft = new SmartsForm();
        }

        this.model.project = this.model.currentProject;

        return this.model.currentProject;
    }

    filterSitesByProject(sites: Site[] | null | undefined): Site[] {
        const project = this.model?.project;
        if (!sites || !project?.id) {
            return [];
        }

        const projectIds = project.isSummaryProject
            ? (project.consumesFromProjectIds ?? []).filter((projectId: string) =>
                this.model?.projects?.some((availableProject: Project) => availableProject.id === projectId && availableProject.active)
            )
            : [project.id];

        return sites.filter((site: Site) =>
            site.projects?.some((projectId: string) => projectIds.includes(projectId))
        );
    }

    filterActiveSitesByProject(sites: Site[] | null | undefined): Site[] {
        return this.filterSitesByProject(sites).filter((site: Site) => site.active);
    }

    saveDraft(app: App, project: Project): void {
        this.model.savingOrPublishing = true;

        this._projectDataService.saveDraft(app, project)
            .subscribe({
                next: (project: Project) => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.LAYOUT_DRAFT_SAVED, SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                    this.model.project = project;
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ERROR_ACTION_ITEM('layout', 'saving'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                complete: () => { }
            })
            .add(() => {
                this.model.savingOrPublishing = false;
            });
    }

    setProjects(projects: Project[]): void {
        this.initModel();
        this.model.projects = projects;
        this.projects.next(this.model.projects);
        this.model.projectsLoading = false;
        this._projectsReady.next(true);
    }

    setProject(project: Project): void {
        this.model.currentProject = project;
        if (project) {
            this._breadcrumbService.createBreadcrumbs(this.model.currentProject, [...(this.model.projects ?? []), ...(this.model.adminProjects ?? [])]);
        }
        if (project?.id && !project.isSummaryProject) {
            this.model.lastRegularProjectId = project.id;
        }
        this.project.next(this.model.currentProject);
        this.model.project = this.model.currentProject;
        this.projectChangedNotification();
        this.selectedProjectChangedNotification();
    }

    disconnectProject(): void {
        this.initModel();
        this.model.project = new Project();
        this.model.currentProject = new Project();
        this.project.next(null);
    }

    disconnectProjects(): void {
        this.model.projects = new Array<Project>();
        this.model.adminProjects = new Array<Project>();
        this.projects.next(null);
    }

    getAllUserProjects(app: App): void {
        if (this.model.projectsLoading) {
            return;
        }

        this.model.projectsLoading = true;
        this.projectsLoading.next(true);
        this.model.projects = [];
        this.model.adminProjects = [];

        this._projectDataService.getAllUserProjects(app)
            .subscribe({
                next: (userProjectLists: UserProjectLists) => {
                    this.model.adminProjects = userProjectLists.projects.filter(project => {
                        return userProjectLists.adminProjectIds.includes(project.id);
                    });
                    this.setProjects(userProjectLists.projects);

                    this.setDefaultProject();

                    this._projectsReady.next(true);
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEMS_LOADED(app.aliases.project.plural), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ERROR_ACTION_ITEM(app.aliases.project.plural.toLowerCase(), 'getting'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                complete: () => { }
            })
            .add(() => {
                this.projectsLoading.next(false);
                this.model.projectsLoading = false;
            });
    }

    setDefaultProject(): void {
        const projects = this.selectableProjects.filter((project: Project) => project.active);
        const defaultProject = this.getDefaultProject(projects);
        if (!defaultProject) {
            this.setProject(new Project());
            return;
        }

        const currentProjectId = this.model.project?.id;
        if (!currentProjectId || !projects.some((project: Project) => project.id === currentProjectId)) {
            this.setProject(defaultProject);
        }
    }

    ensureAllowedProjectSelected(allowSummaryProject: boolean): void {
        const currentProject = this.model?.project;

        if (!currentProject?.id) {
            return;
        }

        if (!this.validateAdmin() && !currentProject.isSummaryProject) {
            return;
        }

        const allowedProjects = this.selectableProjects
            .filter((project: Project) => project.active && (allowSummaryProject || !project.isSummaryProject));

        const defaultProject = this.getDefaultProject(allowedProjects);
        if (!defaultProject || allowedProjects.some((project: Project) => project.id === currentProject.id)) {
            return;
        }

        this.setProject(defaultProject);
    }

    ensureRegularProjectSelected(): void {
        this.ensureAllowedProjectSelected(false);
    }

    ensureSelectableProjectSelected(): void {
        this.ensureAllowedProjectSelected(true);
    }

    summaryProjectConsumesFromSomeSiteProject(summaryProject: Project | undefined, siteProjectIds: string[] | undefined): boolean {
        if (!summaryProject?.consumesFromProjectIds?.length || !siteProjectIds?.length) {
            return false;
        }

        return summaryProject.consumesFromProjectIds.some((projectId: string) =>
            siteProjectIds.includes(projectId)
        );
    }

    setProjectById(projectId: string | null): void {
        if (!projectId) {
            return;
        }

        const project = this.model?.projects?.find(p => p.id === projectId);
        if (project) {
            this.setProject(project);
        }
    }

    addUpdateProject(project: Project): void {
        if (this.model.projects.find((existingProject: Project) => existingProject.id === project.id)) {
            const index = this.model.projects.findIndex((existingProject: Project) => existingProject.id === project.id);
            this.model.projects[index] = project;
        }
        else {
            this.model.projects.push(project);
        }

        if (this.validateAdmin()) {
            this.updateAdminProject(project);
            this.setProjects([...this.model.adminProjects]);
        }
        else {
            this.setProjects([...this.model.projects]);
        }
    }

    updateCurrentProjectUISchema(uiSchema: FormElement): void {
        if (!this.model.currentProject?.JSONForms) {
            return;
        }
        if (!this.isUISchemaUpdated(uiSchema, this.model.currentProject?.JSONForms?.UISchema)) {
            return;
        }
        this.model.currentProject.JSONForms.UISchema = uiSchema;
    }

    isUISchemaUpdated(newSchema: FormElement, oldSchema: FormElement): boolean {
        return JSON.stringify(newSchema) !== JSON.stringify(oldSchema);
    }

    updateAdminProject(project: Project): void {
        if (this.model.adminProjects.find((existingProject: Project) => existingProject.id === project.id)) {
            const index = this.model.adminProjects.findIndex((existingProject: Project) => existingProject.id === project.id);
            this.model.adminProjects[index] = project;
        }
        else {
            this.model.adminProjects.push(project);
        }

    }

    publishProject(app: App, project: Project): void {
        this.model.savingOrPublishing = true;

        this._projectDataService.publishProject(app, project)
            .subscribe({
                next: (project: Project) => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.LAYOUT_PUBLISHED, SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                    this.model.project = project;

                    this.addUpdateProject(project);
                    this.setProject(project);
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ERROR_ACTION_ITEM('layout', 'publishing'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                complete: () => { }
            })
            .add(() => {
                this.model.savingOrPublishing = false;
            });
    }

    updateProjectWithFilter(app: App, project: Project, filter: string[] = null): Observable<Project> {
        const project$ = new Subject<Project>();

        this._projectDataService.updateProjectWithFilter(app, project, filter)
            .subscribe({
                next: (project: Project) => {
                    this.addUpdateProject(project);
                    if (this.model.currentProject?.id === project.id || !this.model.currentProject?.id && project.active) {
                        if (!project.active) {
                            if (this.validateAdmin()) {
                                project = this.getDefaultProject(this.model.adminProjects);
                            }
                            else {
                                project = this.getDefaultProject(this.model.projects);
                            }
                        }
                        else {
                            if (this.validateAdmin()) {
                                project = this.model.adminProjects.find((proj: Project) => proj.id === project.id);
                            }
                            else {
                                project = this.model.projects.find((proj: Project) => proj.id === project.id);
                            }
                        }
                    }
                    else {
                        project = this.model.currentProject;
                    }
                    this.setProject(project);
                    project$.next(project);
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_UPDATED(app.aliases.project.singular), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ERROR_ACTION_ITEM(app.aliases.project.singular.toLocaleLowerCase(), 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                complete: () => { }
            });

        return project$.asObservable();
    }

    updateProjectOrders(app: App, orderByProjectId: Record<string, number>): void {
        this._projectOrdersUpdating.next(true);
        this._projectDataService.updateProjectOrders(app, orderByProjectId).pipe(
            finalize(() => {
                this._projectOrdersUpdating.next(false);
            })
        ).subscribe({
            next: (projects: Project[]) => {
                projects.forEach((updatedProject: Project) => {
                    this.addUpdateProject(updatedProject);
                });
                this._snackBar.open(
                    SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS(app.aliases.project.plural, 'reordered'),
                    SNACKBAR_CONSTANTS.CLOSE,
                    { duration: 3000 }
                );
            },
            error: () => {
                this._snackBar.open(
                    SNACKBAR_CONSTANTS.ERROR_ACTION_ITEM(app.aliases.project.singular.toLocaleLowerCase(), 'reordering'),
                    SNACKBAR_CONSTANTS.CLOSE,
                    { duration: 3000 }
                );
            }
        });
    }

    validateProjectDeactivation(): boolean {
        const minimumActiveProject = 1;

        const projects = this.selectableProjects
            .filter((project: Project) => project.active && !project.isSummaryProject);
        if (projects.length === minimumActiveProject) {
            return false;
        }
        else if (projects.length > 0) {
            return true;

        }
        return false;
    }

    generateNewFieldIdsForProject(project: Project): void {
        this._smartsFormBuilderLogicService.generateNewFieldIdsForSection(project.layoutDraft.Sections);
        this._uiSchemaBuilderUtility.build(project.layoutDraft);
        this._schemaBuilderUtility.build(project.layoutDraft);
        project.JSONForms = cloneDeep(project.layoutDraft);
        this._breadcrumbService.createBreadcrumbs(project);
    }

    copyLayout(app: App, layoutTarget: Project): void {
        layoutTarget.layoutDraft = this.model.currentProject.layoutDraft;

        this.generateNewFieldIdsForProject(layoutTarget);

        this._projectDataService.updateProject(app, layoutTarget)
            .subscribe({
                next: (project: Project) => {
                    this.addUpdateProject(project);
                    this.setProject(project);
                    this._snackBar.open(SNACKBAR_CONSTANTS.LAYOUT_COPIED, SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ERROR_ACTION_ITEM(app.aliases.project.singular.toLocaleLowerCase(), 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000 });
                },
                complete: () => { }
            });
    }

    projectChangedNotification(): void {
        this._projectChanged.next(true);
    }

    selectedProjectChangedNotification(): void {
        this._selectedProjectChanged.next(true);
    }

    dragAndDropNotification(): void {
        this._dragAndDropUpdate.next(true);
    }

    createItem(app: App, project: Project): void {
        this.model.savingOrPublishing = true;

        this._projectDataService.createProject(app, project)
            .subscribe({
                next: (project: Project) => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_DATA_SAVED(app.aliases.project.singular), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                    this.addUpdateProject(project);
                    if (this.validateAdmin() && this.model.adminProjects.length === 1) {
                        this.setProject(this.model.adminProjects.filter(proj => proj.active)[0]);
                    }
                    else if (!this.validateAdmin() && this.model.projects.length === 1) {
                        this.setProject(this.model.projects.filter(proj => proj.active)[0]);
                    }
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ERROR_ACTION_ITEM(`the ${app.aliases.project.singular.toLocaleLowerCase()}`, 'creating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                }
            })
            .add(() => {
                this.model.savingOrPublishing = false;
            });
    }

    updateItem(app: App, project: Project): void {
        this.model.savingOrPublishing = true;

        this._projectDataService.updateProject(app, project)
            .subscribe({
                next: (updatedProject: Project) => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_DATA_SAVED(app.aliases.project.singular), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                    this.addUpdateProject(updatedProject);
                    this.setProject(updatedProject);
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ERROR_ACTION_ITEM(`the ${app.aliases.project.singular.toLocaleLowerCase()}`, 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                }
            })
            .add(() => {
                this.model.savingOrPublishing = false;
            });
    }

    createSummaryProject(app: App, project: Project): Observable<Project> {
        return this.saveSummaryProject(app, this._projectDataService.createSummaryProject(app, project));
    }

    updateSummaryProject(app: App, project: Project): Observable<Project> {
        return this.saveSummaryProject(app, this._projectDataService.updateSummaryProject(app, project));
    }

    exportLayout(app: App, project: Project): void {
        this.model.exportingLayout = true;

        this._projectDataService.exportLayout(app, project)
            .subscribe({
                next: ((response) => {
                    const link = document.createElement("a");
                    link.setAttribute("href", URL.createObjectURL(response));
                    link.setAttribute("download", `${project.name}-ExportedLayout.txt`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }),
                error: (() => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.LAYOUT_EXPORT_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                }),
                complete: (() => { }),
            })
            .add(() => {
                this.model.exportingLayout = false;
            });
    }

    uploadLayout(app: App, project: Project, formData: FormData): void {
        this.model.uploadingLayout = true;

        this._projectDataService.uploadLayout(app, project, formData)
            .subscribe({
                next: ((response: Project) => {
                    this.generateNewFieldIdsForProject(response);
                    this.addUpdateProject(response);
                    this.setProject(response);
                    this._snackBar.open(SNACKBAR_CONSTANTS.LAYOUT_UPLOADED, SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                }),
                error: (() => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.LAYOUT_UPLOAD_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                }),
                complete: (() => { }),
            })
            .add(() => {
                this.model.uploadingLayout = false;
            });
    }

    // An Admin screen has an "admin" path segment. Matching the segment rather than the text keeps an app whose URL
    // starts with "admin" (for example /app/administration/map) from being treated as an Admin screen.
    validateAdmin(): boolean {
        const path = (this._router.url ?? '').split(/[?#]/)[0];
        return path.split('/').includes('admin');
    }

    doesAnyFormDataExist(): boolean {
        // eslint-disable-next-line
        for (let i = 0; i < this.model.projects.length; i++) {
            if (this.model.projects[i].JSONForms?.Sections?.length > 0) {
                return true;
            }
        }

        return false;
    }

    setCurrentProjectRulesLoaded(): void {
        this.model.currentProject['rulesLoaded'] = true;
        this.addUpdateProject(this.model.currentProject);
    }

    setSummaryProjectAsCurrent(): Project | null {
        const summaryProject = this.model.projects.find(project => project.isSummaryProject);
        if (summaryProject) {
            this.setProject(summaryProject);
        }

        return summaryProject ?? null;
    }

    deleteSummaryProject(app: App, summaryProjectId: string): Observable<void> {
        const deleted$ = new Subject<void>();
        const summaryProjectAlias = `summary ${app.aliases.project.singular.toLocaleLowerCase()}`;

        this.model.savingOrPublishing = true;

        this._projectDataService.deleteSummaryProject(app)
            .subscribe({
                next: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS(summaryProjectAlias, 'deleted'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                    this.removeProject(summaryProjectId);
                    deleted$.next();
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR(summaryProjectAlias, 'deleting'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
                }
            })
            .add(() => {
                this.model.savingOrPublishing = false;
                deleted$.complete();
            });

        return deleted$.asObservable();
    }

    private getDefaultProject(projects: Project[]): Project | undefined {
        const activeProjects = projects.filter((proj: Project) => proj.active);

        return activeProjects.find((proj: Project) => proj.isSummaryProject)
            ?? this.getDefaultRegularProject(activeProjects);
    }

    private getDefaultRegularProject(projects: Project[]): Project | undefined {
        const regularProjects = projects.filter((proj: Project) => proj.active && !proj.isSummaryProject);

        return regularProjects.find((proj: Project) => proj.id === this.model.lastRegularProjectId)
            ?? regularProjects[0];
    }

    private removeProject(projectId: string): void {
        this.model.projects = this.model.projects.filter((proj: Project) => proj.id !== projectId);
        this.model.adminProjects = this.model.adminProjects.filter((proj: Project) => proj.id !== projectId);

        this.setProjects(this.validateAdmin() ? this.model.adminProjects : this.model.projects);
        this.setDefaultProject();
    }

    private saveSummaryProject(app: App, projectSaveRequest$: Observable<Project>): Observable<Project> {
        const summaryProject$ = new Subject<Project>();

        this.model.savingOrPublishing = true;

        projectSaveRequest$
            .subscribe({
                next: (savedProject: Project) => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_DATA_SAVED(`Summary ${app.aliases.project.singular.toLocaleLowerCase()}`), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                    this.addUpdateProject(savedProject);
                    this.setProject(savedProject);
                    summaryProject$.next(savedProject);
                },
                error: (error: HttpErrorResponse) => {
                    const summaryProjectAlias = `summary ${app.aliases.project.singular.toLocaleLowerCase()}`;
                    const message = error.status === HttpStatusCode.Conflict
                        ? SNACKBAR_CONSTANTS.SUMMARY_PROJECT_CONFLICT(summaryProjectAlias)
                        : SNACKBAR_CONSTANTS.ERROR_ACTION_ITEM(`the ${summaryProjectAlias}`, 'saving');
                    this._snackBar.open(message, SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                }
            })
            .add(() => {
                this.model.savingOrPublishing = false;
                summaryProject$.complete();
            });

        return summaryProject$.asObservable();
    }
}
